import {
    AfterViewInit, ChangeDetectorRef, DestroyRef, Directive, ElementRef, inject, AfterViewChecked
} from "@angular/core";
import { SubformComponent } from "../../shared/subform/subform.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

/**
 * Directive that automatically sets focus on an HTML element when it becomes available in the view
 *
 * This directive handles two scenarios:
 * 1. Standard elements: Focus is applied immediately after the view initializes
 * 2. Elements within SubformComponent: Focus is applied after the subform opens
 *
 * Usage:
 * ```html
 * <input type="text" mgdAutoFocus />
 * ```
 *
 * When used inside a SubformComponent, the directive waits for the subform's opened event
 * before applying focus, ensuring the element is visible and ready to receive focus
 */
@Directive({ selector: "[mgdAutoFocus]" })
export class AutoFocusDirective implements AfterViewInit, AfterViewChecked {
    /**
     * Reference to the host element that will receive focus
     */
    private readonly _elemRef = inject<ElementRef<HTMLElement>>(ElementRef, { self: true });

    /**
     * Optional reference to parent SubformComponent if this directive is used within one
     */
    private readonly _subform? = inject(SubformComponent, {
        host: true,
        optional: true
    });

    /**
     * Service for managing subscription cleanup
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Change detector for triggering change detection after focus
     */
    private readonly _cdRef = inject(ChangeDetectorRef);

    /**
     * Flag indicating whether focus should be applied during the next view check cycle
     */
    private _focusAfterViewCheck = false;

    /**
     * Initializes focus behavior after the view has been initialized
     *
     * If the element is within a SubformComponent, subscribes to the opened event
     * to apply focus when the subform becomes visible. Otherwise, applies focus immediately
     */
    ngAfterViewInit(): void {

        if (this._subform) {
            this._subform.opened.pipe(
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(() => {
                this._focusAfterViewCheck = true;
            });
        }
        else {
            this._focus();
        }
    }

    /**
     * Applies focus during the view check cycle if the flag is set
     *
     * This is used when the element is within a SubformComponent to ensure
     * focus is applied after the subform has fully rendered and is visible
     */
    ngAfterViewChecked(): void {
        if (this._focusAfterViewCheck) {
            this._focusAfterViewCheck = false;
            this._focus();
        }
    }

    /**
     * Sets focus on the host element and triggers change detection
     */
    private _focus(): void {
        this._elemRef.nativeElement.focus();
        this._cdRef.detectChanges();
    }

}
